import {FormattedMessage} from "react-intl";
import React, {useEffect, useState} from 'react';
import {createColumnHelper, SortingFn} from "@tanstack/react-table";

import {
    useResidenceExplorerStore,
    useFilteredObjects,
    selectSelectedObjectId,
    selectSetup
} from "features/residenceExplorer/store/useResidenceExplorerStore";
import {addDevCounter} from "./renderers";
import {SearchResult} from "features/search/types";
import {getPriceListTableColumns} from './tableConfig';
import {isEmpty} from "features/services/utils/helpers";
import {getDefaultSorting, getFilteredData} from './utils';
import {useAuth} from "features/authentication/hooks/useAuth";
import {getFormattedMessage} from "features/services/utils/language-helper";
import {DisposalForm, ObjectStatus, ResidenceType} from "features/search/filters/enums";
import {LinkStyle} from "features/ui/enums";
import {gtmEventsPriceListObjectClick} from "features/services/googleTagManager/events";

import './style.scss';

import {A} from "@atoms/LinksAndButtons/A";
import {Table} from "@organisms/GeneralComponents/Table";
import { Tags, TagDesign } from "@atoms/Tags/Tags";
import {DataTableCardType} from "@organisms/GeneralComponents/Table/CardView";

interface Props {
    constructionForm: ResidenceType;
    disposalForm: DisposalForm;
    isCoOwning: boolean;
    isMultiProject: boolean;
    currentNumberOfRooms?: number;
    objectId?: string;
}

export const PriceListV2: React.FunctionComponent<Props> = ({isCoOwning, disposalForm, constructionForm, isMultiProject, currentNumberOfRooms, objectId }) => {
    const setup = useResidenceExplorerStore(selectSetup);
    const {user} = useAuth();
    const objects = useFilteredObjects();
    const selectedObjectId = useResidenceExplorerStore(selectSelectedObjectId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic sort config from getDefaultSorting with varying sort methods
    const [defaultSorted, setDefaultSorted] = useState<any>(null);

    useEffect(() => {
        if(!objects) return;

        // Logic for setting columns and defaultSorted
        setDefaultSorted(getDefaultSorting(constructionForm, currentNumberOfRooms));
        setColumnVisibility({
            'pricelist-id': !isUserEmpty(),
        })
    }, [objects, user]);

    const onInternalClick = (data: SearchResult) => (_e: React.SyntheticEvent) => {
        if (data && data.url && data.apartmentNumber && setup) gtmEventsPriceListObjectClick(setup.stageName, data.url, data.apartmentNumber)();
    };
    const isUserEmpty = () => {
        return (user === null || user === undefined || isEmpty(user));
    };

    const sortObjectStatusFn: SortingFn<SearchResult> = (rowA, rowB, _columnId) => {
        const statusA = rowA.original.objectStatus;
        const statusB = rowB.original.objectStatus;
        const statusOrder = [ObjectStatus.FOR_SALE, ObjectStatus.RESERVED, ObjectStatus.BOOKED, ObjectStatus.SOLD];
        const sortValue1 = statusOrder.indexOf(statusA) - statusOrder.indexOf(statusB);
        const sortValue2 = (rowA.original.rooms - rowB.original.rooms) * 10000;
        const sortValue3 = (rowA.original.price - rowB.original.price) / 1000;
        const sortValue = (sortValue1 * 100000) + sortValue2 + sortValue3;
        
        return sortValue;
    }

    const columnHelper = createColumnHelper<SearchResult>();
    const columns = [
        columnHelper.accessor(`objectStatus`, {
            id: 'pricelist-status',
            cell: info => {return <Tags 
                tagDesign={TagDesign.DOT} 
                status={info.getValue()} 
                className='status' 
            />},
            header: () => addDevCounter(1) + getFormattedMessage('jm.components.Pricelist.status'),
            sortingFn: sortObjectStatusFn,
        }),

        ...getPriceListTableColumns(objects, user, isCoOwning, isMultiProject, disposalForm, constructionForm),

        columnHelper.accessor(`url`, {
            id: 'row-url',
            cell: info => {return <A href={info.getValue()} variant={LinkStyle.UNSTYLED} className="table__row__link" title={getFormattedMessage('jm.components.Object.goToObject', {objectNumber: info.row.original.apartmentNumber })} >
                <span className='visually-hidden'>
                    <FormattedMessage id='jm.components.Object.goToObject' values={{objectNumber: info.row.original.apartmentNumber }} />
                </span>
            </A>},
            header: () => '',
        }),
    ];
    const [columnVisibility, setColumnVisibility] = useState({
        'pricelist-id': !isUserEmpty(), //hide this column by default
    });

    if (!defaultSorted || objects.length === 0 || !columns) return null;

    return (
        <div className="price-list">
            <Table
                id="price-list-table"
                data={getFilteredData(objects, setup!.hideSoldObjects)}
                columns={columns}
                columnVisibility={columnVisibility}
                foldableCount={5}
                cardType={DataTableCardType.PRICELIST}
                currentItem={selectedObjectId}
                currentObjectId={objectId}
                onRowClick={onInternalClick} 
                defaultSort={'pricelist-status'}
            />
        </div>
    );
};
